/* eslint-disable indent */
import { BannerHalfBlurred } from 'components/common/BannerHalfBlurred'
import { useEmblaPagination } from 'contexts'
import { EmblaPaginationProvider } from 'contexts/providers/EmblaPaginationProvider'
import useEmblaCarousel from 'embla-carousel-react'
import useTranslation from 'next-translate/useTranslation'
import React, {
  ComponentProps,
  PropsWithChildren,
  memo,
  useEffect,
  useState,
} from 'react'

type Slide = {
  content: React.ReactNode
} & Omit<
  ComponentProps<typeof BannerHalfBlurred>,
  'type' | 'isFocused' | 'renderImageWhenOnScreen' | 'src'
>

interface ArtistHeaderMobileCarouselProps {
  slides: Slide[]
}

export const ArtistHeaderMobileCarousel = memo(
  ({ slides }: ArtistHeaderMobileCarouselProps) => {
    const [carousel, carouselApi] = useEmblaCarousel(
      {
        loop: true,
        direction: 'ltr',
        inViewThreshold: 1,
      },
      []
    )

    return (
      <div className='relative w-full'>
        <div ref={carousel} className='w-full overflow-hidden'>
          <div
            className='flex touch-pan-y'
            style={{ backfaceVisibility: 'hidden' }}
          >
            {slides.map(({ content, ...slide }, index) => {
              return (
                <div key={index} className='min-w-0 flex-[0_0_100%]'>
                  {content}
                </div>
              )
            })}
          </div>
        </div>

        <div className='mx-auto flex w-full justify-center gap-x-3 py-4'>
          <EmblaPaginationProvider emblaApi={carouselApi}>
            <DotButtons />
          </EmblaPaginationProvider>
        </div>
      </div>
    )
  }
)

ArtistHeaderMobileCarousel.displayName = 'BannerCarousel'

const DotButtons = () => {
  const { scrollSnaps, selectedIndex, onPaginate } = useEmblaPagination()

  return (
    <>
      {scrollSnaps.map((_, index) => (
        <DotButton
          key={index}
          onClick={() => onPaginate(index)}
          selected={index === selectedIndex}
          index={index}
        />
      ))}
    </>
  )
}

type PropType = PropsWithChildren<
  React.DetailedHTMLProps<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
  >
> & { selected: boolean; index: number }

const DotButton: React.FC<PropType> = props => {
  const { children, selected, index, ...restProps } = props
  const { t } = useTranslation('home')

  return (
    <button
      type='button'
      {...restProps}
      className={[
        'h-[3px] w-5 rounded sm:h-3.5 sm:w-3.5 sm:rounded-full',
        selected ? 'bg-[#14142B]' : 'bg-[#14142B1A]',
      ].join(' ')}
    >
      <p className='sr-only'>{t('view_slide_x', { x: index + 1 })}</p>

      {children}
    </button>
  )
}
